/**
 * TrackingPlatformFormDialog - Cadastro/edição de plataforma de rastreamento.
 *
 * Campos: Nome*, Fornecedor (Pessoas › Fornecedor), URL de Acesso, Plataforma Ativa.
 * Validação definitiva em `upsert_stock_tracking_platform`.
 *
 * RELEASE: 2026-10-11 v1.0.0 - Versão inicial.
 */
import { useEffect, useState } from 'react';
import { Loader2, Monitor } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { FieldLabel } from '@/components/common/FormSection';
import { PersonSelector } from '@/components/persons/PersonSelector';
import { isValidPlatformUrl, useUpsertTrackingPlatform, type TrackingPlatform } from '@/hooks/useTrackingPlatforms';

interface Props {
  open: boolean;
  platform: TrackingPlatform | null;
  onOpenChange: (open: boolean) => void;
}

export function TrackingPlatformFormDialog({ open, platform, onOpenChange }: Props) {
  const upsert = useUpsertTrackingPlatform();
  const [name, setName] = useState('');
  const [supplier, setSupplier] = useState<string | null>(null);
  const [supplierLabel, setSupplierLabel] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [active, setActive] = useState(true);

  // Carrega os dados a cada abertura
  useEffect(() => {
    if (!open) return;
    setName(platform?.name ?? '');
    setSupplier(platform?.supplier_person_id ?? null);
    setSupplierLabel(platform?.supplier_name ?? null);
    setUrl(platform?.access_url ?? '');
    setActive(platform?.is_active ?? true);
  }, [open, platform]);

  const urlOk = isValidPlatformUrl(url);
  const canSave = name.trim().length > 0 && urlOk && !upsert.isPending;

  const save = () => {
    if (!canSave) return;
    upsert.mutate(
      { id: platform?.id, data: { name: name.trim(), supplier_person_id: supplier, access_url: url.trim() || null, is_active: active } },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !upsert.isPending && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
        <PremiumDialogHeader icon={Monitor} title={platform ? 'Editar Plataforma' : 'Nova Plataforma'} />
        <div className="space-y-4 px-6 py-5">
          <div className="space-y-1.5">
            <FieldLabel required>Nome</FieldLabel>
            <Input value={name} maxLength={120} placeholder="Ex.: Plataforma 1"
              onChange={(e) => setName(e.target.value.replace(/[<>]/g, ''))} />
          </div>
          <div className="space-y-1.5">
            <FieldLabel>Fornecedor</FieldLabel>
            <PersonSelector value={supplier} role="supplier" placeholder="Sem fornecedor" selectedLabel={supplierLabel}
              onSelect={(id) => { setSupplier(id); if (!id) setSupplierLabel(null); }} />
          </div>
          <div className="space-y-1.5">
            <FieldLabel>URL de Acesso</FieldLabel>
            <Input value={url} maxLength={500} placeholder="https://" aria-invalid={!urlOk}
              onChange={(e) => setUrl(e.target.value)} />
            {!urlOk && <p className="text-[0.8rem] font-medium text-destructive">Informe uma URL iniciando com http:// ou https://</p>}
          </div>
          <label className="flex items-center gap-3 text-sm font-medium">
            <Switch checked={active} onCheckedChange={setActive} /> Plataforma Ativa
          </label>
        </div>
        <DialogFooter className="border-t bg-muted/30 px-6 py-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={upsert.isPending}>Cancelar</Button>
          <Button onClick={save} disabled={!canSave}>
            {upsert.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            {platform ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
